export type OccupancyRow = {
  weekday: number
  start_time: string
  course_id: string
  course_name: string
  session_count: number
  full_session_count: number
  booked_sum: number
  capacity_sum: number
  avg_occupancy: number
}

export type OccupancyCourse = {
  courseId: string
  courseName: string
  startTime: string
  sessionCount: number
  fullCount: number
  bookedSum: number
  capacitySum: number
  occupancy: number
}

export type OccupancyCell = {
  weekday: number
  hour: number
  sessionCount: number
  fullCount: number
  fullShare: number
  occupancy: number
  courses: OccupancyCourse[]
}

export type OccupancyGrid = {
  hours: number[]
  cells: Map<string, OccupancyCell>
}

export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const
export const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa',
  7: 'So',
}

export const OFTEN_FULL_SHARE = 0.75
export const OCCUPANCY_STEPS = 5

export function formatStartTime(value: string): string {
  return value.slice(0, 5)
}

export function formatHourSlot(hour: number): string {
  return `${String(hour).padStart(2, '0')}–${String((hour + 1) % 24).padStart(2, '0')}`
}

export function occupancyCellKey(weekday: number, hour: number): string {
  return `${weekday}-${hour}`
}

function ratio(part: number, total: number): number {
  return total > 0 ? Math.min(part / total, 1) : 0
}

// Sessions are grouped into hour slots by start time (18:00 and 18:30 share the 18–19 slot),
// so schedules that shift a little between weeks still line up.
// Cells weight occupancy by capacity, so a 20-place course counts more than a 4-place one.
export function buildOccupancyGrid(rows: OccupancyRow[]): OccupancyGrid {
  const cells = new Map<string, OccupancyCell>()

  for (const row of rows) {
    const startTime = formatStartTime(row.start_time)
    const hour = Number(startTime.slice(0, 2))
    const key = occupancyCellKey(row.weekday, hour)
    const cell = cells.get(key) ?? {
      weekday: row.weekday,
      hour,
      sessionCount: 0,
      fullCount: 0,
      fullShare: 0,
      occupancy: 0,
      courses: [],
    }

    cell.courses.push({
      courseId: row.course_id,
      courseName: row.course_name,
      startTime,
      sessionCount: row.session_count,
      fullCount: row.full_session_count,
      bookedSum: row.booked_sum,
      capacitySum: row.capacity_sum,
      occupancy: ratio(row.booked_sum, row.capacity_sum),
    })
    cells.set(key, cell)
  }

  for (const cell of cells.values()) {
    const booked = cell.courses.reduce((sum, course) => sum + course.bookedSum, 0)
    const capacity = cell.courses.reduce((sum, course) => sum + course.capacitySum, 0)
    cell.sessionCount = cell.courses.reduce((sum, course) => sum + course.sessionCount, 0)
    cell.fullCount = cell.courses.reduce((sum, course) => sum + course.fullCount, 0)
    cell.fullShare = ratio(cell.fullCount, cell.sessionCount)
    cell.occupancy = ratio(booked, capacity)
    cell.courses.sort(
      (a, b) => a.startTime.localeCompare(b.startTime) || a.courseName.localeCompare(b.courseName, 'de'),
    )
  }

  const hours = [...new Set([...cells.values()].map((cell) => cell.hour))].sort((a, b) => a - b)

  return { hours, cells }
}

// 0 = empty, 1..5 = (0, 20%), [20, 40%), [40, 60%), [60, 80%), [80, 100%].
export function occupancyStep(value: number): number {
  if (value <= 0) return 0
  return Math.min(OCCUPANCY_STEPS, Math.floor(value * OCCUPANCY_STEPS) + 1)
}

export function isOftenFull(cell: Pick<OccupancyCell, 'sessionCount' | 'fullShare'>): boolean {
  return cell.sessionCount >= 2 && cell.fullShare >= OFTEN_FULL_SHARE
}

export type TrainingFrequencyBucket = 'none' | 'lt1' | '1to2' | '2to3' | '3plus'

export const TRAINING_FREQUENCY_LABELS: Record<TrainingFrequencyBucket, string> = {
  none: 'Kein Training',
  lt1: 'Unter 1× pro Woche',
  '1to2': '1–2× pro Woche',
  '2to3': '2–3× pro Woche',
  '3plus': '3× oder öfter pro Woche',
}

export function shareOf(part: number, total: number): number {
  return total > 0 ? part / total : 0
}
